import React, { useState, useEffect } from "react";
import { Globe, RefreshCw, Clock, AlertTriangle, Shield, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * ApplicationAnalysis component for displaying DAST (Dynamic Application Security Testing) reports
 * @returns {JSX.Element}
 */
export default function ApplicationAnalysis() {
  // State for DAST report
  const [dastReport, setDastReport] = useState(null);
  const [dastReportLoading, setDastReportLoading] = useState(false);
  
  // State for expanded vulnerabilities
  const [expandedItems, setExpandedItems] = useState({});
  
  // Fetch DAST report when component mounts
  useEffect(() => {
    fetchDastReport();
  }, []);
  
  // Function to fetch the DAST report
  const fetchDastReport = async () => {
    try {
      setDastReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.DAST_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        setDastReport(response.data);
      } else {
        setDastReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch DAST report'
        });
      }
      setDastReportLoading(false);
    } catch (error) {
      setDastReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setDastReportLoading(false);
    }
  };
  
  // Toggle expanded state for an item
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Calculate report stats
  const calculateReportStats = (report) => {
    // Check if report exists and has the correct structure
    if (!report || !report.report) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    // Handle the specific structure of DAST report
    const vulnerabilities = 
      (report.report.vulnerabilities) ? report.report.vulnerabilities : 
      (Array.isArray(report.report)) ? report.report : [];
      
    if (!Array.isArray(vulnerabilities)) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    const stats = { total: vulnerabilities.length, high: 0, medium: 0, low: 0, info: 0 };
    
    vulnerabilities.forEach(vuln => {
      if (vuln.severity) {
        const severity = vuln.severity.toLowerCase();
        if (severity === 'critical' || severity === 'high') {
          stats.high++;
        } else if (severity === 'medium') {
          stats.medium++;
        } else if (severity === 'low') {
          stats.low++;
        } else {
          stats.info++;
        }
      }
    });
    
    return stats;
  };
  
  // Calculate stats for the report
  const dastStats = calculateReportStats(dastReport);
  
  // Render vulnerability list
  const renderVulnerabilityList = (vulnerabilities) => {
    // Handle the specific structure of DAST report
    const vulnsArray = 
      (vulnerabilities && vulnerabilities.vulnerabilities) ? vulnerabilities.vulnerabilities : 
      (Array.isArray(vulnerabilities)) ? vulnerabilities : [];
      
    if (!vulnsArray || !Array.isArray(vulnsArray)) {
      return <p className="text-gray-700">No vulnerabilities to display</p>;
    }
    
    if (vulnsArray.length === 0) {
      return <p className="text-gray-700">No vulnerabilities found. Great job!</p>;
    }
    
    return (
      <div className="space-y-4">
        {vulnsArray.map((vuln, index) => {
          // Generate a unique ID for each vulnerability
          const vulnId = `dast-vuln-${index}`;
          const isExpanded = expandedItems[vulnId] || false;
          
          // Determine severity class
          let severityClass = '';
          let severityBg = '';
          if (vuln.severity) {
            const severity = vuln.severity.toLowerCase();
            if (severity === 'critical' || severity === 'high') {
              severityClass = 'text-red-700';
              severityBg = 'bg-red-50';
            } else if (severity === 'medium') {
              severityClass = 'text-orange-700';
              severityBg = 'bg-orange-50';
            } else if (severity === 'low') {
              severityClass = 'text-yellow-700';
              severityBg = 'bg-yellow-50';
            } else {
              severityClass = 'text-blue-700';
              severityBg = 'bg-blue-50';
            }
          }
          
          return (
            <div key={vulnId} className={`border rounded-lg ${severityBg}`}>
              <div 
                className={`p-4 cursor-pointer flex justify-between items-center ${severityClass}`}
                onClick={() => toggleExpand(vulnId)}
              >
                <div className="flex items-start space-x-2">
                  <div className="pt-0.5">
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <h3 className="font-medium">{vuln.name || vuln.message || vuln.description || 'Unnamed Vulnerability'}</h3>
                    <p className="text-sm opacity-80">
                      {typeof vuln.location === 'object' 
                        ? `${vuln.location.hostname}${vuln.location.path}` 
                        : (vuln.path || vuln.url || 'Unknown location')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-3">{vuln.severity || 'Unknown'}</span>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              
              {isExpanded && (
                <div className="p-4 border-t bg-white text-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-medium mb-1">Description</h4>
                      <p className="text-sm">{vuln.description || vuln.message || 'No description available'}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium mb-1">Details</h4>
                      <ul className="space-y-1 text-sm">
                        {vuln.solution && (
                          <li><span className="font-medium">Solution:</span> {vuln.solution}</li>
                        )}
                        {vuln.cve && (
                          <li>
                            <span className="font-medium">CVE:</span> 
                            <a 
                              href={`https://cve.mitre.org/cgi-bin/cvename.cgi?name=${vuln.cve}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              {vuln.cve} <ExternalLink size={12} className="inline" />
                            </a>
                          </li>
                        )}
                        {vuln.confidence && (
                          <li><span className="font-medium">Confidence:</span> {vuln.confidence}</li>
                        )}
                        {vuln.identifiers && Array.isArray(vuln.identifiers) && vuln.identifiers.length > 0 && (
                          <li>
                            <span className="font-medium">Identifiers:</span>
                            <ul className="ml-4 list-disc">
                              {vuln.identifiers.map((id, i) => (
                                <li key={i}>{id.type}: {id.name}</li>
                              ))}
                            </ul>
                          </li>
                        )}
                        {vuln.details && typeof vuln.details === 'object' && (
                          <li>
                            <span className="font-medium">Additional Details:</span>
                            <ul className="ml-4 list-disc">
                              {Object.entries(vuln.details).map(([key, detail], i) => (
                                <li key={i}>
                                  {key}: {typeof detail === 'object' && detail.value 
                                    ? detail.value 
                                    : (typeof detail === 'string' ? detail : JSON.stringify(detail))}
                                </li>
                              ))}
                            </ul>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  {vuln.evidence && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-1">Evidence</h4>
                      {typeof vuln.evidence === 'object' ? (
                        <div className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                          {vuln.evidence.summary && (
                            <p className="font-medium mb-1">Summary: {vuln.evidence.summary}</p>
                          )}
                          {vuln.evidence.request && (
                            <div className="mb-2">
                              <p className="font-medium">Request:</p>
                              <p className="ml-2">Method: {vuln.evidence.request.method || 'N/A'}</p>
                              <p className="ml-2">URL: {vuln.evidence.request.url || 'N/A'}</p>
                            </div>
                          )}
                          {vuln.evidence.response && (
                            <div>
                              <p className="font-medium">Response:</p>
                              <p className="ml-2">Status: {vuln.evidence.response.status_code} {vuln.evidence.response.reason_phrase || ''}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">{JSON.stringify(vuln.evidence, null, 2)}</pre>
                      )}
                    </div>
                  )}
                  
                  {vuln.links && Array.isArray(vuln.links) && vuln.links.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-1">References</h4>
                      <ul className="space-y-1 text-sm">
                        {vuln.links.map((link, i) => (
                          <li key={i}>
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {link.name || link.url} <ExternalLink size={12} className="inline" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Application Security Analysis</h1>
        <button 
          onClick={fetchDastReport} 
          disabled={dastReportLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center"
        >
          <RefreshCw size={16} className={`mr-2 ${dastReportLoading ? "animate-spin" : ""}`} />
          {dastReportLoading ? "Refreshing..." : "Refresh Reports"}
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h2 className="text-xl font-medium text-gray-800 mb-4">DAST Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{dastStats.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{dastStats.high}</div>
              <div className="text-sm text-red-600">High</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">{dastStats.medium}</div>
              <div className="text-sm text-orange-600">Medium</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{dastStats.low}</div>
              <div className="text-sm text-yellow-600">Low</div>
            </div>
          </div>
          
          {dastReport?.timestamp && (
            <div className="flex items-center text-xs text-gray-500 mt-4">
              <Clock size={12} className="mr-1" />
              Last scan: {new Date(dastReport.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      
      {/* DAST Report Card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
              <Globe size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">DAST Vulnerabilities</h2>
              <p className="text-sm text-gray-500">Dynamic Application Security Testing results</p>
            </div>
          </div>
          <div className="flex items-center">
            <span className="mr-4 flex items-center">
              <Shield className="text-green-500 mr-1" size={16} />
              <span className="text-sm font-medium">Web Application Security</span>
            </span>
          </div>
        </div>
        
        <div className="p-6">
          {dastReportLoading ? (
            <div className="flex justify-center py-20">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-16 h-16 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ) : dastReport?.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-6 text-red-700 text-center">
              <AlertTriangle size={40} className="mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">Error retrieving DAST report</h3>
              <p className="text-sm">{dastReport.message || 'Failed to load DAST data. Please try refreshing the report.'}</p>
              <button 
                onClick={fetchDastReport}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          ) : dastReport?.status === 'pending' ? (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 text-blue-700 text-center">
              <Clock size={40} className="mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">DAST analysis in progress</h3>
              <p className="text-sm">The security scan is still running. Please check back later.</p>
              <div className="mt-4 w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                <div className="h-2 rounded-full bg-blue-600 animate-pulse" style={{ width: '60%' }}></div>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Vulnerability Findings</h3>
                {renderVulnerabilityList(dastReport?.report)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
